// Two corrections to the local point fabric derives a control transform from.
//
// Both land on the same function. controlsUtils.getLocalPoint turns the pointer
// into a coordinate relative to the anchor the transform is pinned on, and
// everything a resize does is computed from it. It is a module-local function in
// the fabric bundle - the copy on fabric.controlsUtils is a reference the transform
// code does not go through - so it cannot be replaced. Its only input is
// target.toLocalPoint, which can be, and which fabric calls from nowhere else. So
// the corrections go in there: work out the point fabric ought to be scaling from,
// then hand it the value that makes its own arithmetic reproduce that point.
//
// --- 1. the padding typo -----------------------------------------------------
//
// Fabric draws the border and the icons `padding` away from the element, and takes
// that offset back off per axis. The four lines read:
//
//     if (localPoint.x >= padding)  { localPoint.x -= padding; }
//     if (localPoint.x <= -padding) { localPoint.x += padding; }
//     if (localPoint.y >= padding)  { localPoint.y -= padding; }
//     if (localPoint.y <= padding)  { localPoint.y += padding; }   <- missing minus
//
// The last line is meant to mirror the second. Because it does not, the two y
// branches run in sequence on the same value: a y between padding and 2*padding
// loses the padding to the first and gets it straight back from the second, while a
// y past 2*padding only loses it once. The mapping steps by a whole padding as y
// crosses 2*padding. The local y during a resize is roughly the element's height
// plus the padding, so on a short element that threshold sits inside the drag: the
// element jumps backwards by a tenth of its scale in one frame and then carries on.
//
// --- 2. a handle crossing its anchor ------------------------------------------
//
// The anchor of a bottom-right resize is the top-left corner, which on a
// shirt-sized element is only a few tens of pixels away - right where the duplicate
// and delete icons sit. Drag past it and fabric swaps transform.originY to the
// opposite edge and negates the scale. Two things follow. The negative scale is not
// stored as one: Object.prototype._set turns it into a flipX/flipY toggle with a
// positive size, so the element mirrors. And wrapWithFixedAnchor, which measured
// the anchor *before* the handler ran, then repositions the element from the *new*
// origin using that old anchor point - so the element jumps by its own height.
//
// lockScalingFlip stops both, but by returning false for the whole transform, on
// both axes, for as long as the pointer stays past the anchor. A resize died the
// moment it wandered over the icons above the element.
//
// Keeping the pointer on its own side of the anchor avoids all of it. Nothing
// swaps, so nothing mirrors and nothing moves; the element simply pins at its
// smallest and keeps following the other axis, and grows again on the way back.

// Fabric's padding rule, as its x branches already apply it correctly. This is the
// value both axes should end up with.
export function unpad(value, padding) {
	if (value >= padding) return value - padding;
	if (value <= -padding) return value + padding;
	return value;
}

// What fabric's x branches do to a value - correct, and the inverse of encodeX.
export function fabricPadX(value, padding) {
	let out = value;
	if (out >= padding) out -= padding;
	if (out <= -padding) out += padding;
	return out;
}

// What fabric's y branches do to a value, typo included.
export function fabricPadY(value, padding) {
	let out = value;
	if (out >= padding) out -= padding;
	if (out <= padding) out += padding;
	return out;
}

// The input that makes fabric's x branches yield `intended`. Both arms are
// invertible over the range that matters, so aiming at the right one is enough.
export function encodeX(intended, padding) {
	return intended >= 0 ? intended + padding : intended - padding;
}

// The same for the y branches: `value - padding` above 2*padding, `value + padding`
// below padding, identity between. An intended value above the padding is reached
// from above 2*padding, anything else from below padding.
export function encodeY(intended, padding) {
	return intended > padding ? intended + padding : intended - padding;
}

// Not zero: fabric reads the direction with Math.sign, and Math.sign(0) is 0, which
// counts as a change of direction exactly as -1 would - so a handle parked exactly
// on its anchor would swap the origin after all.
export const ANCHOR_EPSILON = 0.001;

// Hold a coordinate on the side of the anchor its handle started on. A centred
// transform has no side - both directions are legal - so it is left alone.
export function clampToAnchorSide(value, origin) {
	if (origin === 'left' || origin === 'top') return Math.max(ANCHOR_EPSILON, value);
	if (origin === 'right' || origin === 'bottom') return Math.min(-ANCHOR_EPSILON, value);
	return value;
}

// Whether the fabric in use still has the y typo. Probed rather than assumed, so
// upgrading fabric drops that half of the correction instead of double-applying it.
// getLocalPoint is exported even though the transform code does not go through the
// export, which is what makes this measurable from outside.
export function hasPaddingJump(fabric) {
	if (!fabric || !fabric.controlsUtils || !fabric.controlsUtils.getLocalPoint) return false;
	const padding = 10;
	// Between padding and 2*padding: the band the two y branches cancel over.
	const probe = 15;
	const target = {
		padding,
		canvas: { getZoom: () => 1 },
		controls: { probe: { offsetX: 0, offsetY: 0 } },
		toLocalPoint: () => new fabric.Point(probe, probe)
	};
	let out;
	try {
		out = fabric.controlsUtils.getLocalPoint({ target, corner: 'probe' }, 'left', 'top', 0, 0);
	} catch (e) {
		return false;
	}
	// x loses its padding, y does not.
	return out.x === probe - padding && out.y === probe;
}

export default function fixControlTransforms(fabric) {
	if (!fabric || !fabric.Object || fabric.Object.prototype.__controlTransformsFixed) return false;

	// The anchor clamp is wanted either way; only the y encoding depends on the
	// typo still being there. A fabric that handles y like x gets the x encoder.
	const encodeYAxis = hasPaddingJump(fabric) ? encodeY : encodeX;
	const toLocalPoint = fabric.Object.prototype.toLocalPoint;

	fabric.Object.prototype.toLocalPoint = function (point, originX, originY) {
		const local = toLocalPoint.call(this, point, originX, originY);
		// The same padding getLocalPoint is about to use.
		const zoom = (this.canvas && typeof this.canvas.getZoom === 'function' && this.canvas.getZoom()) || 1;
		const padding = (this.padding || 0) / zoom;

		// The point fabric should be working from...
		const x = clampToAnchorSide(unpad(local.x, padding), originX);
		const y = clampToAnchorSide(unpad(local.y, padding), originY);
		// ...encoded so that its own branches arrive at exactly that.
		local.x = encodeX(x, padding);
		local.y = encodeYAxis(y, padding);
		return local;
	};

	fabric.Object.prototype.__controlTransformsFixed = true;
	return true;
}
